import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useNotification } from "../context/NotificationContext";
import { useClient } from "../context/ClientContext";
import GooeySVG from "../shared/components/folderSVG";

// Only one field: Email
interface RequestResetPasswordFormData {
  email: string;
}

// Example SimpleSlider reuse
const SimpleSlider = () => {
   const images = [
    "/assets/images/slider1.png",
    "/assets/images/slider1.png",
    "/assets/images/slider1.png",
  ];
  const [currentIndex, setCurrentIndex] = useState(0);

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <div className="relative w-full overflow-hidden">
      <img
        src={images[currentIndex]}
        alt="Slide"
        className="w-full h-fit items-center justify-center"
      />
      <div className="absolute bottom-2 left-0 right-0 flex justify-center space-x-2">
        <button
          onClick={handlePrev}
          className="px-3 py-1 bg-white text-gray-50 rounded shadow hover:bg-primary-200"
        >
          Prev
        </button>
        <button
          onClick={handleNext}
          className="px-3 py-1 bg-white text-gray-50 rounded shadow hover:bg-primary-200"
        >
          Next
        </button>
      </div>
    </div>
  );
};

// Example API call for password reset
// Adjust the endpoint as appropriate for your backend
const requestResetPasswordAPI = async (client: any, data: RequestResetPasswordFormData) => {
  try {
    return await client.post("/auth/request-reset-password", data);
  } catch (error: any) {
    throw error;
  }
};

const RequestResetPasswordPage = () => {
  const navigate = useNavigate();
  const notification = useNotification();
  const client = useClient("AUTH_SERVICE");

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RequestResetPasswordFormData>();

  const [loading, setLoading] = useState(false);

  const onSubmit = async (data: RequestResetPasswordFormData) => {
    setLoading(true);
    try {
      // POST request to your reset-password endpoint
      const response = await requestResetPasswordAPI(client, data);

      // If the backend responds success
      notification.success(
        "A password reset link has been sent to your email."
      );
      navigate("/login");
    } catch (err: any) {
      notification.error(
        err.message || "An error occurred while sending reset link."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="min-h-screen bg-primary-50 flex items-center justify-center relative">
      {/* Optional Gooey filter */}
      <GooeySVG />

      <div className="container mx-auto p-4 flex items-center justify-center h-full">
        {/* Two side-by-side "cards," each constrained by max-w-lg */}
        <div className="flex space-x-0 lg:space-x-4">
          {/* LEFT COLUMN - Form */}
          <div className="flex-none bg-white rounded-lg shadow-lg overflow-hidden max-w-lg p-8">
            <div className="text-center mb-8">
               
               <a href="https://www.serceaproject.org"> 
                <img
                  className="mx-auto w-24 mb-3"
                  src="/assets/images/sercea-2.png"
                  alt="Sercea Project Logo"
                  title="Sercea Project"
                  
                />
              </a>
              <h4 className="text-xl font-extrabold text-primary-600 tracking-wider leading-tight">
                Strengthening Ethics and Responsible Conduct of Clinical Trial
                in East and Sub-Sahara Africa
              </h4>
            </div>

            {/* Reset Password Form */}
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              <p className="mb-4 text-base text-gray-700">
                Enter your email below to reset your password
              </p>

              {/* Email Input */}
              <div className="mb-4">
                <label
                  htmlFor="email"
                  className="block text-sm font-semibold text-gray-700"
                >
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  className="mt-1 w-full border border-gray-300 rounded-md shadow-sm
                             focus:ring-primary-600 focus:border-primary-600 p-2"
                  {...register("email", {
                    required: "Email is required",
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: "Invalid email address",
                    },
                  })}
                />
                {errors.email && (
                  <p className="text-red-600 text-xs mt-1">
                    {errors.email.message}
                  </p>
                )}
              </div>

              {/* Submit Button */}
              <div className="mb-4 pt-2 text-center">
                <button
                  type="submit"
                  disabled={loading}
                  className={`inline-block w-full rounded px-6 py-2 text-xs font-medium uppercase leading-normal text-white
                              shadow-md transition duration-150 ease-in-out
                              bg-primary-600 hover:bg-primary-700 
                              disabled:bg-gray-400 disabled:cursor-not-allowed`}
                >
                  {loading ? "Sending link..." : "Send Reset Link"}
                </button>
              </div>

              {/* Go Back to Login */}
              <div className="flex items-center justify-center">
                <p className="mb-0 mr-2 text-sm text-gray-700">
                  Remembered your password?
                </p>
                <button
                  type="button"
                  onClick={() => navigate("/login")}
                  className={`inline-block rounded border-2 border-primary-600 px-4 py-1 text-xs font-medium uppercase leading-normal text-primary-600
                              hover:bg-primary-50 focus:outline-none 
                              transition duration-150 ease-in-out`}
                >
                  Login
                </button>
              </div>
            </form>
          </div>

          {/* RIGHT COLUMN - Slider */}
          <div className="hidden lg:flex flex-none max-w-lg bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="w-full bg-gradient-to-r from-primary-600 via-pink-500 to-purple-500 p-6">
              <SimpleSlider />
              <div className="p-6 text-center bg-gray-100 w-full">
                <p className="text-sm text-gray-700 font-medium leading-relaxed">
                  <span className="font-semibold">
                    The Open Data Access Platform for Clinical Studies is a web-based system designed to enhance data portability, knowledge sharing, and evidence generation in clinical research.
                  </span>
                  <span className="font-semibold">
                    {" "}
                     It provides researchers, policymakers, and institutions with transparent and accessible data on clinical studies.
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RequestResetPasswordPage;
